import { f7 } from 'framework7-react';
import { useCallback, useMemo, useState } from 'react';
import {
  CarInformationItemType,
  EstimateDetailInfoType,
  EstimateResultDataType,
  InsuranceResultInfoType,
  RepairRequestIDInfoType,
} from '../../../types/bodyAndPaint';
import { RepairEstimateResult } from './estimateResult/repairEstimateResult';
import { usePostCancelRepairCheckInReserve } from './hook/api/usePostCancelRepairCheckInReserve';
import { usePostUpdateInsuranceInfo } from './hook/api/usePostUpdateInsuranceInfo';
import { useInsuranceState } from './hook/state/useInsuranceState';
import { useEstimateResultData } from './hook/useEstimateResultData';
import { useRepairDetailData } from './hook/useRepairDetailData';
import { useStorageItem } from './hook/useStorageItem';
import { AddInsuranceInfoField } from './insuranceEntry/addInsuranceInfoField';
import { PageTemplateComponent } from './util/common/pageTemplate/pageTemplate';
import { ModalSheet } from './util/modalSheet';
import { paths } from '@/config/paths';
import { store } from '@/config/store';

export const RepairEstimateResultPage = () => {
  const idInfo = useMemo(() => {
    const { authInfo, customerInfo, reservation } = store.state;
    return {
      affiliateID: customerInfo?.m_affiliate_id ?? 0,
      customerID: authInfo?.m_customer_id ?? 0,
      reservationID: reservation?.reservation_id ?? 0,
      stockCarID: reservation?.t_stock_car_id ?? 0,
      unitID: customerInfo?.m_unit_id ?? 0,
    };
  }, []);

  const { repairDetailData, initialInsuranceState, onReload } =
    useRepairDetailData(idInfo);

  const status = useMemo(
    () => repairDetailData?.state_info?.status ?? -1,
    [repairDetailData],
  );
  const inspectionID = useMemo(
    () => repairDetailData?.ids?.inspectionID ?? 0,
    [repairDetailData],
  );
  const saleID = useMemo(
    () => repairDetailData?.ids?.saleID ?? 0,
    [repairDetailData],
  );
  const carInfo = useMemo(
    () =>
      repairDetailData?.reserve_info?.car_info ??
      ({} as CarInformationItemType),
    [repairDetailData],
  );
  const factoryName = useMemo(
    () => repairDetailData?.reserve_info?.factory_name ?? '',
    [repairDetailData],
  );
  const checkInDate = useMemo(
    () => repairDetailData?.reserve_info?.check_in_date ?? ({} as Date),
    [repairDetailData],
  );
  const { estimateResultData } = useEstimateResultData({
    status,
    affiliateID: idInfo.affiliateID,
    customerID: idInfo.customerID,
    inspectionID,
    saleID,
    carInfo,
    checkInDate,
  }) as { estimateResultData: EstimateResultDataType };

  const { setStorageItem: setCheckDamagedImage } = useStorageItem<{
    fromPage: string;
    inspectionID: number;
  }>({
    key: 'repairCheckDamagedImage',
  });
  const { setStorageItem: setItemSelect } = useStorageItem<{
    ids: RepairRequestIDInfoType;
    insuranceResult?: InsuranceResultInfoType;
    estimateDetailList: EstimateDetailInfoType;
    repairRequestItem: {
      status: number;
      carName: string;
      factoryName: string;
    };
  }>({
    key: 'repairItemSelect',
  });
  const { postRequest: noRepairRequest } = usePostCancelRepairCheckInReserve({
    m_customer_id: idInfo.customerID,
    t_pm_car_inspection_id: inspectionID,
  });

  const [openInsuranceModal, setOpenInsuranceModal] = useState(false);

  const handleClickBack = () => {
    f7.views.main.router.navigate(paths.maintenance);
  };

  const handleClickCloseInsuranceModal = useCallback(
    () => setOpenInsuranceModal(false),
    [],
  );
  const handleClickCheckImage = useCallback(() => {
    setCheckDamagedImage({
      fromPage: paths.bpsRepairEstimateResult,
      inspectionID,
    });
    f7.views.main.router.navigate(paths.bpsRepairDamagedImage);
  }, [inspectionID, setCheckDamagedImage]);

  const handleClickRepairSelect = useCallback(() => {
    setItemSelect({
      ids: estimateResultData.ids ?? ({} as RepairRequestIDInfoType),
      insuranceResult:
        estimateResultData.insuranceResultInfo ??
        ({} as InsuranceResultInfoType),
      estimateDetailList:
        estimateResultData.estimateDetailList ?? ([] as EstimateDetailInfoType),
      repairRequestItem: {
        carName: carInfo?.car_name ?? '',
        factoryName: factoryName,
        status: status,
      },
    });
    f7.views.main.router.navigate(paths.bpsRepairItemSelect);
  }, [
    status,
    setItemSelect,
    estimateResultData,
    carInfo?.car_name,
    factoryName,
  ]);

  const handleClickOpenInsuranceModal = useCallback(() => {
    setOpenInsuranceModal(true);
  }, []);

  const handleClickNoRepair = useCallback(() => {
    noRepairRequest().then(() => {
      f7.views.main.router.navigate(paths.bpsRepairInfoDetail);
    });
  }, [noRepairRequest]);

  const {
    insuranceState,
    insuranceSetState,
    insuranceSelectList,
    insuranceError,
    pickerInfo,
    disabled,
    hasDeductible,
    isInsurance,
    postParameters,
  } = useInsuranceState({
    ids: {
      customerID: idInfo.customerID,
      inspectionID: inspectionID,
      affiliateID: idInfo.affiliateID,
    },
    initial: initialInsuranceState,
  });

  const { postRequest: updateInsurance } = usePostUpdateInsuranceInfo({
    params: postParameters,
  });

  const handleClickAddInsurance = useCallback(() => {
    updateInsurance().then(() => {
      onReload();
      handleClickCloseInsuranceModal();
      f7.views.main.router.navigate(paths.bpsRepairInfoDetail);
    });
  }, [updateInsurance, onReload, handleClickCloseInsuranceModal]);

  return (
    <PageTemplateComponent
      pageName={'checkInReserve'}
      title={'傷・凹み修理見積もり結果'}
      onBack={handleClickBack}
    >
      <RepairEstimateResult
        data={estimateResultData ?? ({} as EstimateResultDataType)}
        onClickCheckImage={handleClickCheckImage}
        onClickRepairSelect={handleClickRepairSelect}
        onOpenInsuranceModal={handleClickOpenInsuranceModal}
        onClickNoRepair={handleClickNoRepair}
      />
      <ModalSheet
        title={'保険の登録'}
        open={openInsuranceModal}
        onClose={() => setOpenInsuranceModal(false)}
      >
        <AddInsuranceInfoField
          cancelEntry={handleClickCloseInsuranceModal}
          state={insuranceState}
          setState={insuranceSetState}
          selectList={insuranceSelectList}
          error={insuranceError}
          pickerInfo={pickerInfo}
          addInsurance
          hasDeductible={hasDeductible}
          isInsurance={isInsurance}
          disabled={disabled}
          onRegisterInsurance={handleClickAddInsurance}
        />
      </ModalSheet>
    </PageTemplateComponent>
  );
};
